import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useBlocker, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { AmountInput } from '@/components/amount-input';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { ApiError, api } from '@/lib/api';
import { useBankAccounts, type BankAccount } from '@/lib/banking';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { exponentOf } from '@/lib/currencies';
import { formatAmount } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import type { JournalEntry } from '@/lib/journal-api';
import { useOrgId } from '@/lib/org';

export const Route = createFileRoute('/banking/transfers/new')({ component: NewTransfer });

/** Screen B5 (document 06): + New › Transfer between Bank and Credit card accounts. */
function NewTransfer() {
  const orgId = useOrgId();
  const banks = useBankAccounts();
  const me = useMe();
  if (!orgId) return <EmptyState title="Select a client" />;
  if (me.data && !me.data.isStaff)
    return <EmptyState title="Only accounting staff can record transfers" />;
  if (banks.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (banks.isError)
    return <EmptyState title="Could not load bank accounts" hint={banks.error.message} />;
  return <TransferForm key={orgId} banks={banks.data} />;
}

function TransferForm({ banks }: { banks: BankAccount[] }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [date, setDate] = useState(todayIso());
  const [memo, setMemo] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const leaving = useRef(false);
  const dirty = Boolean(from || to || amount || toAmount || memo.trim());
  useBlocker({
    shouldBlockFn: () =>
      dirty &&
      !leaving.current &&
      !window.confirm('You have unsaved changes. Leave without saving?'),
    enableBeforeUnload: () => dirty && !leaving.current,
  });

  const label = (b: BankAccount) =>
    `${b.code} · ${b.name} (${b.currency} ${formatAmount(b.ledgerBalance)})`;
  const close = () => void navigate({ to: '/banking' });
  const fromBank = banks.find((b) => b.accountId === from);
  const toBank = banks.find((b) => b.accountId === to);
  /** Accounts in different currencies: both amounts are entered and the rate follows from them. */
  const crossCurrency = Boolean(fromBank && toBank && fromBank.currency !== toBank.currency);

  const save = useMutation({
    mutationFn: (_andNew: boolean) =>
      api<JournalEntry>(`/orgs/${orgId}/banking/transfers`, {
        method: 'POST',
        body: JSON.stringify({
          fromAccountId: from,
          toAccountId: to,
          date,
          amount,
          ...(crossCurrency ? { toAmount } : {}),
          memo: memo.trim() || null,
        }),
      }),
    onSuccess: async (entry, andNew) => {
      await qc.invalidateQueries();
      toast(`Transfer saved as journal entry no. ${entry.entryNo}.`, {
        link: { to: `/accounting/journal-entries/${entry.id}`, label: 'View' },
      });
      if (andNew) {
        setFrom('');
        setTo('');
        setAmount('');
        setToAmount('');
        setMemo('');
        setErrors({});
      } else {
        leaving.current = true;
        void navigate({ to: '/banking' });
      }
    },
  });

  const submit = (andNew: boolean) => {
    if (save.isPending) return;
    const next: Record<string, string> = {};
    if (!from) next['from'] = 'Choose the account the money leaves';
    if (!to) next['to'] = 'Choose the account the money goes to';
    if (from && to && from === to) next['to'] = 'Choose a different account';
    if (!amount) next['amount'] = 'Enter an amount';
    if (crossCurrency && !toAmount)
      next['toAmount'] = `Enter the amount received in ${toBank!.currency}`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) next['date'] = 'Enter the date';
    setErrors(next);
    if (!Object.keys(next).length) save.mutate(andNew);
  };
  useHotkeys({
    'ctrl+alt+s': () => submit(false),
    'ctrl+alt+d': () => submit(true),
    'ctrl+alt+x': close,
  });

  return (
    <OverlayPage
      title="Transfer"
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          Cancel
        </Button>
      }
      footerRight={
        <>
          <Button variant="outline" disabled={save.isPending} onClick={() => submit(true)}>
            Save and new
          </Button>
          <Button disabled={save.isPending} onClick={() => submit(false)}>
            {save.isPending ? 'Saving…' : 'Save and close'}
          </Button>
        </>
      }
    >
      <div className="flex max-w-3xl flex-col gap-4">
        <ErrorBanner>
          {save.error
            ? save.error instanceof ApiError
              ? save.error.message
              : String(save.error)
            : null}
        </ErrorBanner>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Transfer funds from *" error={errors['from']}>
            {(id) => (
              <NativeSelect
                id={id}
                value={from}
                aria-invalid={Boolean(errors['from'])}
                onChange={(e) => setFrom(e.target.value)}
              >
                <option value="">Choose an account</option>
                {banks.map((b) => (
                  <option key={b.accountId} value={b.accountId}>
                    {label(b)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Transfer funds to *" error={errors['to']}>
            {(id) => (
              <NativeSelect
                id={id}
                value={to}
                aria-invalid={Boolean(errors['to'])}
                onChange={(e) => setTo(e.target.value)}
              >
                <option value="">Choose an account</option>
                {banks
                  .filter((b) => b.accountId !== from)
                  .map((b) => (
                    <option key={b.accountId} value={b.accountId}>
                      {label(b)}
                    </option>
                  ))}
              </NativeSelect>
            )}
          </Field>
          <Field
            label={`Transfer amount${fromBank ? ` (${fromBank.currency})` : ''} *`}
            error={errors['amount']}
          >
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 w-full rounded-md border border-input bg-card px-3 text-right text-sm"
                value={amount}
                exp={fromBank ? exponentOf(fromBank.currency) : 2}
                invalid={Boolean(errors['amount'])}
                onValueChange={setAmount}
              />
            )}
          </Field>
          {crossCurrency ? (
            <Field
              label={`Amount received (${toBank!.currency}) *`}
              error={errors['toAmount']}
              hint="The exchange rate is taken from the two amounts"
            >
              {(id) => (
                <AmountInput
                  id={id}
                  className="h-9 w-full rounded-md border border-input bg-card px-3 text-right text-sm"
                  value={toAmount}
                  exp={exponentOf(toBank!.currency)}
                  invalid={Boolean(errors['toAmount'])}
                  onValueChange={setToAmount}
                />
              )}
            </Field>
          ) : null}
          <Field label="Date *" error={errors['date']}>
            {(id) => (
              <DateInput
                id={id}
                value={date}
                onChange={setDate}
                invalid={Boolean(errors['date'])}
              />
            )}
          </Field>
          <Field label="Memo" className="md:col-span-2">
            {(id) => <Textarea id={id} value={memo} onChange={(e) => setMemo(e.target.value)} />}
          </Field>
        </div>
        <p className="text-sm text-muted-foreground">
          Posts a journal entry: debit the account the money goes to, credit the account it leaves.
          When the bank statements come in, both statement lines match this transfer.
        </p>
      </div>
    </OverlayPage>
  );
}
